import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import games from "virtual:obean-games";
import type { Board } from "../net/board.js";
import { badgesOf, loadBoard, playsLine } from "../net/board.js";

/**
 * The built-in games as picture cards (founder, 2026-10-15): one way to choose a game, alone
 * (`/solo`) or in a room. Every game in this build is there, the lab's marked, so a new game
 * shows up everywhere at once.
 */
export function GameCards(props: {
  /** The game chosen now, if any (shown pressed). */
  current?: string | undefined;
  disabled?: boolean;
  onPick: (gameId: string) => void;
}) {
  // The game board (#377): what others play most, trending, new. A nicety: absent, nothing shows.
  const board = useSignal<Board | null>(null);
  useEffect(() => {
    let current = true;
    void loadBoard().then((b) => {
      if (current) board.value = b;
    });
    return () => {
      current = false;
    };
  }, []);
  return (
    <div class="game-cards">
      {games.map((g) => (
        <button
          type="button"
          key={g.id}
          class="game-card"
          aria-pressed={props.current === undefined ? undefined : props.current === g.id}
          disabled={props.disabled}
          onClick={() => {
            props.onPick(g.id);
          }}
        >
          <img src={g.art} alt="" width={320} height={180} loading="lazy" />
          <strong>{g.displayName}</strong>
          <span>{g.tagline}</span>
          {board.value && <BoardLine board={board.value} gameId={g.id} />}
          {g.stage === "lab" && <span class="lab">In the lab: not tuned yet</span>}
        </button>
      ))}
    </div>
  );
}

/** A game's badges and its plays this week, if the board has them. */
function BoardLine({ board, gameId }: { board: Board; gameId: string }) {
  const badges = badgesOf(board, gameId);
  const line = playsLine(board, gameId);
  if (badges.length === 0 && !line) return null;
  return (
    <span class="board">
      {badges.map((b) => (
        <b key={b} class="board-badge">
          {b}
        </b>
      ))}
      {line && <small>{line}</small>}
    </span>
  );
}
